/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import {
  CardMembership,
  Create,
  Home,
  Person,
} from "@mui/icons-material";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Tooltip,
  Typography,
} from "@mui/joy";
import { ReactNode, memo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import useGoogleLoginStore from "../stores/googleLoginStore";
import useLanguageStore from "../stores/languageStore";
import { getLocalizedPath } from "../utils/urlUtils";
import { closeSidebar } from "./sidebar";

export const Navigation = memo(function Navigation({
  collapsed = false,
}: {
  collapsed?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguageStore();
  const userStatus = useGoogleLoginStore(
    (state) =>
      (state as { userStatus: { isAuthenticated: boolean } }).userStatus,
  );
  const setGoogleLoginModalVisible = useGoogleLoginStore(
    (state) => (state as { setVisible: (v: boolean) => void }).setVisible,
  );
  const setSuccessCallback = useGoogleLoginStore(
    (state) =>
      (state as { setSuccessCallback: (cb: () => void) => void })
        .setSuccessCallback,
  );
  const setReturnCallback = useGoogleLoginStore(
    (state) =>
      (state as { setReturnCallback: (cb: () => void) => void })
        .setReturnCallback,
  );

  // Helper function to build language-prefixed paths
  const getLangPath = (path: string): string => {
    return getLocalizedPath(path, currentLanguage);
  };

  return (
    <List
      size="sm"
      sx={{
        gap: 0.5,
        "--List-nestedInsetStart": "30px",
        "--ListItem-radius": (theme) => theme.vars.radius.sm,
      }}
    >
      <NavItem
        path={getLangPath("/")}
        label={t("navigation.home")}
        icon={<Home />}
        collapsed={collapsed}
        navigate={navigate}
      />
      <NavItem
        path={getLangPath("/creator-center")}
        label={t("navigation.myCharacters")}
        icon={<Create />}
        collapsed={collapsed}
        navigate={navigate}
        onClick={() => {
          closeSidebar();
          if (!userStatus?.isAuthenticated) {
            setGoogleLoginModalVisible(true);
            setSuccessCallback(() => {
              navigate(getLangPath("/creator-center"));
            });
            setReturnCallback(() => {});
            return;
          }
          navigate(getLangPath("/creator-center"));
        }}
      />
      <NavItem
        path={getLangPath("/user-persona")}
        label={t("navigation.myPersonas")}
        icon={<Person />}
        collapsed={collapsed}
        navigate={navigate}
        onClick={() => {
          closeSidebar();
          if (!userStatus?.isAuthenticated) {
            setGoogleLoginModalVisible(true);
            setSuccessCallback(() => {
              navigate(getLangPath("/user-persona"));
            });
            setReturnCallback(() => {});
            return;
          }
          navigate(getLangPath("/user-persona"));
        }}
      />
      {userStatus?.isAuthenticated ? (
        <NavItem
          path={getLangPath("/membership")}
          label={t("navigation.membership")}
          icon={<CardMembership />}
          collapsed={collapsed}
          navigate={navigate}
        />
      ) : null}
    </List>
  );
});

function NavItem(props: NavItemProps): JSX.Element {
  const location = useLocation();

  // Check if current path matches the nav item path
  // Also check path without language prefix for backwards compatibility
  const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}/, "");
  const propsPathWithoutLang = props.path.replace(/^\/[a-z]{2}/, "");
  const isSelected =
    !!useMatch(props.path) ||
    location.pathname === props.path ||
    pathWithoutLang === propsPathWithoutLang;

  const button = (
    <ListItemButton
      component={Link}
      selected={!!isSelected}
      to={props.path}
      aria-current="page"
      onClick={() => {
        props.onClick?.();
        closeSidebar();
      }}
      sx={
        props.collapsed
          ? { justifyContent: { md: "center" }, px: { md: 1 } }
          : undefined
      }
    >
      <ListItemDecorator
        children={props.icon}
        sx={
          props.collapsed
            ? { minInlineSize: { md: "auto" }, mr: { md: 0 } }
            : undefined
        }
      />
      <ListItemContent
        sx={{ display: props.collapsed ? { xs: "block", md: "none" } : "block" }}
      >
        <Typography component="div" sx={{ fontSize: "1.1rem" }}>
          {props.label}
        </Typography>
      </ListItemContent>
    </ListItemButton>
  );

  return (
    <ListItem>
      {props.collapsed ? (
        <Tooltip title={props.label} placement="right" size="sm">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
}

type NavItemProps = {
  path: string;
  label: ReactNode;
  icon: ReactNode;
  collapsed?: boolean;
  navigate: (path: string) => void;
  onClick?: () => void;
};
